import React, { useState } from 'react';
import { Loader2Icon, CheckCircle2Icon, XCircleIcon, DatabaseIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '../ui/card';
import { ConnectorIcon } from './connector-icon';

interface CreateConnectorSourceToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

export const CreateConnectorSourceToolCall: React.FC<CreateConnectorSourceToolCallProps> = ({
  args,
  argsText,
  result,
  status,
}) => {
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = !isRunning && !isError && result !== undefined;

  let connectorType = '';
  let sourceName = '';
  try {
    const parsedArgs = typeof args === 'object' ? args : JSON.parse(argsText);
    connectorType = parsedArgs?.connector_type || '';
    sourceName = parsedArgs?.name || '';
  } catch (e) {
    // Ignore
  }

  const [showDetail, setShowDetail] = useState(false);

  let isSuccess = false;
  let connectorTitle = '';
  let resultSourceName = '';
  let errorMessage = '';
  if (isComplete && result) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      isSuccess = parsed?.success === true;
      connectorTitle = parsed?.connector_title || '';
      resultSourceName = parsed?.name || '';
      errorMessage = parsed?.error || '';
    } catch (e) {
      isSuccess = !result.toLowerCase().includes('error');
    }
  }

  const displayName = resultSourceName || sourceName || connectorTitle || 'data source';
  const hasError = isError || (isComplete && !isSuccess);

  return (
    <Card>
      <div className={cn(
        "p-3",
        isRunning
          ? "bg-primary/10 border-b border-primary/20"
          : "bg-muted/50 border-b border-border"
      )}>
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1">
            {isRunning ? (
              <div className="flex items-center gap-2">
                <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
                <p className="text-sm m-0 text-foreground">
                  Creating data source: <strong>{sourceName || 'source'}</strong>
                </p>
              </div>
            ) : hasError ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <DatabaseIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    Creating data source: <strong>{displayName}</strong>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
                  <p className="text-sm m-0 text-destructive">
                    Failed to create data source
                  </p>
                  {errorMessage && (
                    <button onClick={() => setShowDetail(!showDetail)} className="ml-auto text-muted-foreground hover:text-foreground">
                      {showDetail ? <ChevronUpIcon className="h-3 w-3" /> : <ChevronDownIcon className="h-3 w-3" />}
                    </button>
                  )}
                </div>
                {showDetail && errorMessage && (
                  <p className="text-xs m-0 text-destructive/80 pl-6">{errorMessage}</p>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {connectorType ? (
                  <ConnectorIcon connectorType={connectorType} size={20} />
                ) : (
                  <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                )}
                <p className="text-sm m-0 text-foreground">
                  Created data source: <strong>{displayName}</strong>
                </p>
                {connectorType && (
                  <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0 ml-auto" />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
